import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

import { CmmContainer, CmmSubTitle } from 'components/Dashboard';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import moment from 'moment';
import { useRecoilValue } from 'recoil';
import { authAtom } from '../../../_state';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';

export default function AnalysisAgent() {
  // ========================= 상수 / 헬퍼 =========================
  // 다운로드는 DB에서 가져오므로 API 히스토리에서는 제외
  const EXCLUDE_DIVS = ['desktop_lock', 'desktop_unlock', 'desktop_get'];
  const UPLOAD_DIVS = ['desktop_version', 'desktop_put', 'desktop_newDoc'];
  const DOWNLOAD_DIVS = ['desktop_get'];

  const fixedColumns = ['div', 'regDtt', 'userNm', 'userId', 'sectorNm', 'deptNm'];
  const headerMap = {
    div: '구분',
    regDtt: '이력 날짜',
    userNm: '사용자 이름',
    userId: '사용자 사번',
    sectorNm: '사용자 부문이름',
    deptNm: '사용자 부서이름'
  };

  const sortBySubOrder = (a, b) => (a && a.subOrder ? a.subOrder : 0) - (b && b.subOrder ? b.subOrder : 0);

  const mapDivToKor = (div) => {
    if (!div) return '-';
    if (UPLOAD_DIVS.includes(div)) return '업로드';
    if (DOWNLOAD_DIVS.includes(div)) return '다운로드';
    return div;
  };

  const renderCellValue = (val, key) => {
    if (val === null || val === undefined || val === '') return '-';
    if (key === 'regDtt') {
      const m = moment(val);
      return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : String(val);
    }
    if (key === 'div') return mapDivToKor(String(val));
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return String(val);
    try {
      const s = JSON.stringify(val);
      return s.length > 200 ? s.slice(0, 200) + '…' : s;
    } catch {
      return String(val);
    }
  };

  const nf = new Intl.NumberFormat();

  // 문자열 정규화(공백/대소문자)
  const norm = (s) => (s ?? '').toString().trim().replace(/\s+/g, ' ').toLowerCase();

  // 정렬/필터용 타임스탬프. 행마다 한 번만 계산해 두고 이후엔 숫자 비교만 한다.
  // (비교자 안에서 moment()를 만들면 1만건 정렬에 moment 인스턴스가 수십만 개 생긴다)
  const toTs = (v) => {
    if (!v) return NaN;
    const t = Date.parse(v);
    if (!Number.isNaN(t)) return t;
    const m = moment(v);
    return m.isValid() ? m.valueOf() : NaN;
  };

  // ========================= 상태 =========================
  const auth = useRecoilValue(authAtom);

  const [years, setYears] = useState([]);
  const [year, setYear] = useState(moment().format('YYYY'));
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState(Number(moment().format('MM')));

  const [sectors, setSectors] = useState([]);
  const [depts, setDepts] = useState([]);
  const [sector, setSector] = useState('all'); // sapGroupCode
  const [dept, setDept] = useState('all'); // deptCd

  // 업로드(API) / 다운로드(DB) 분리
  const [history, setHistory] = useState([]); // 업로드
  const [historyError, setHistoryError] = useState(null);
  const [downloads, setDownloads] = useState([]); // 다운로드(정규화 후)
  const [downloadsError, setDownloadsError] = useState(null);

  const [viewType, setViewType] = useState('all'); // 'all' | 'upload' | 'download'

  const [page, setPage] = useState(1); // 1-based
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];

  // 업로드(외부 DIDAS API)는 응답이 수십 초~수 분이라 다운로드와 로딩 상태를 분리한다.
  // 하나로 묶으면 1.5초에 끝난 다운로드가 업로드를 기다리며 화면 전체가 잠긴다.
  const [downloadsLoading, setDownloadsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // 초기화 완료 여부
  const didInitRef = useRef(false);

  // 조회 시퀀스. 느린 업로드 응답이 늦게 도착해 최신 조회 결과를 덮어쓰는 것을 막는다.
  const reqIdRef = useRef(0);

  // ========================= 조회 인덱스 =========================
  // depts/sectors를 행마다 find로 훑으면 O(행수 x 부서수)가 되므로 Map으로 미리 색인한다.
  const sectorNameByCode = useMemo(() => {
    const m = new Map();
    sectors.forEach((x) => {
      if (!m.has(x.sapGroupCode)) m.set(x.sapGroupCode, x.sapGroupName); // find()와 동일하게 첫 항목 우선
    });
    return m;
  }, [sectors]);

  const deptIndex = useMemo(() => {
    // list는 부분일치 폴백용(빈 deptName도 기존 동작 유지를 위해 그대로 둔다)
    const list = depts.map((d) => ({ dept: d, key: norm(d.deptName) }));
    const exact = new Map();
    list.forEach(({ dept: d, key }) => {
      if (key && !exact.has(key)) exact.set(key, d);
    });
    return { exact, list };
  }, [depts]);

  // partname -> { sectorNm, deptName } 해석기.
  // 로그 1만건이라도 고유 부서명은 수백 개뿐이라 이름 단위로 캐시하면 계산량이 급감한다.
  const createDeptResolver = () => {
    const cache = new Map();
    const empty = { sectorNm: '', deptName: '' };
    return (partname) => {
      const key = norm(partname);
      if (!key) return empty;
      const cached = cache.get(key);
      if (cached) return cached;

      let hit = deptIndex.exact.get(key) || null;
      if (!hit) {
        const found = deptIndex.list.find((x) => x.key.includes(key) || key.includes(x.key));
        hit = found ? found.dept : null;
      }
      const resolved = hit ? { sectorNm: sectorNameByCode.get(hit.sapGroupCode) || '', deptName: hit.deptName || '' } : empty;
      cache.set(key, resolved);
      return resolved;
    };
  };

  // ========================= 초기화 / 데이터 로딩 =========================
  const getYears = () => {
    const start = 2024;
    const cur = Number(moment().format('YYYY'));
    const arr = [];
    for (let y = cur; y >= start; y--) arr.push(y);
    setYears(arr);
  };

  const getMonths = () => setMonths(Array.from({ length: 12 }, (_, i) => i + 1));

  const fetchSectorDept = async () => {
    const resp = await axios.get('https://didas.dohwa.co.kr/agent/app/dept/info');
    const data = resp?.data ?? {};
    const sectorList = Array.isArray(data.sectorList) ? data.sectorList.slice().sort(sortBySubOrder) : [];
    const deptList = Array.isArray(data.deptList) ? data.deptList.slice().sort(sortBySubOrder) : [];
    setSectors(sectorList);
    setDepts(deptList);
    return { sectorList, deptList };
  };

  // DIDAS 사용자 정보로 sector/dept 선설정
  const initFromDidasUser = async (deptList) => {
    try {
      let userId = auth?.userid ?? auth?.userId;
      if (!userId) return null;
      if (typeof userId === 'string' && userId.startsWith('999')) userId = userId.slice(3);

      const response = await axios.post('https://didas.dohwa.co.kr/agent/app/user/info', { userId, isCtsPriority: 'Y' }, { headers: { 'Content-Type': 'application/json' } });

      const respSectorCode = response?.data?.userInfo?.sapGroupCd;
      const respMisDeptCd = response?.data?.userInfo?.sapMisDeptCd;

      const matchedDept = Array.isArray(deptList) ? deptList.find((d) => d.sapMisDeptCd === respMisDeptCd) : null;

      const sectorCode = matchedDept?.sapGroupCode ?? respSectorCode ?? 'all';
      const deptCode = matchedDept?.deptCd ?? respMisDeptCd ?? 'all';

      setSector(sectorCode);
      setDept(deptCode);
      return { sectorCode, deptCode };
    } catch (e) {
      console.warn('initFromDidasUser failed:', e?.message || e);
      return null;
    }
  };

  // 첫 로딩: 여기서는 fetch 호출 안 함
  useEffect(() => {
    if (!auth) return;
    (async () => {
      try {
        didInitRef.current = false;
        getYears();
        getMonths();
        const { deptList } = await fetchSectorDept();
        if (auth?.isDidas) await initFromDidasUser(deptList);
      } catch (e) {
        console.error(e);
      } finally {
        didInitRef.current = true;
      }
    })();
  }, [auth]);

  // ========================= API =========================
  // 업로드 히스토리(API) - desktop_get 제외
  const fetchHistory = async (nextSector, nextDept, reqId) => {
    const isStale = () => reqId != null && reqId !== reqIdRef.current;
    try {
      setHistoryError(null);

      const sectorCd = nextSector != null ? nextSector : sector;
      const deptCd = nextDept != null ? nextDept : dept;

      const matched = deptCd && deptCd !== 'all' ? depts.find((d) => d.deptCd === deptCd) : null;
      if (deptCd && deptCd !== 'all' && !matched) {
        console.warn('⚠️ deptCd에 해당하는 부서를 찾을 수 없음:', deptCd);
        if (!isStale()) setHistory([]);
        return;
      }

      const headYn = matched ? matched.headYn : '';

      const payload = {
        year: String(year || ''),
        month: String(month).padStart(2, '0'),
        sapGroupCd: sectorCd === 'all' ? '' : sectorCd,
        deptCode: deptCd === 'all' ? '' : deptCd,
        headYn
      };

      const resp = await axios.get('https://didas.dohwa.co.kr/agent/app/history', {
        params: payload,
        headers: { Accept: 'application/json' }
      });

      if (isStale()) return; // 조건이 바뀐 뒤 도착한 응답은 버린다

      if (Array.isArray(resp?.data)) {
        const cleaned = resp.data.filter((r) => !EXCLUDE_DIVS.includes(r?.div || '')).map((r) => ({ ...r, _ts: toTs(r?.regDtt) }));
        setHistory(cleaned);
      } else {
        setHistory([]);
        setHistoryError('unexpected response format: expected array');
        console.warn('expected array but got', resp?.data);
      }
    } catch (err) {
      console.error(err);
      if (isStale()) return;
      setHistoryError(err?.response?.data?.message || err.message || 'failed to fetch history');
      setHistory([]);
    }
  };

  // 다운로드 히스토리(DB)
  // ✅ 부문 파라미터 제거. 대신 현재 선택 상태에 따라 deptNames(부서명 배열) 전달
  const fetchDownloadsFromDb = async (nextSector, nextDept, reqId) => {
    const isStale = () => reqId != null && reqId !== reqIdRef.current;
    try {
      setDownloadsError(null);

      const sectorCd = nextSector != null ? nextSector : sector; // 코드값 (서버 전송 X)
      const deptCd = nextDept != null ? nextDept : dept; // 코드값

      // 보낼 부서명 배열 결정
      let deptNames = [];
      if (deptCd !== 'all') {
        const d = depts.find((x) => x.deptCd === deptCd);
        if (d?.deptName) deptNames = [d.deptName];
      } else if (sectorCd !== 'all') {
        deptNames = depts.filter((x) => x.sapGroupCode === sectorCd).map((x) => x.deptName);
      }
      // deptNames가 빈 배열이면(=전체) 서버에서 전체 조회

      const payload = {
        year: String(year || ''),
        month: String(month).padStart(2, '0'),
        // 부문은 보내지 않음
        // 서버 구현 호환용: 배열 그대로와 CSV 둘 다 전송(하나는 무시해도 됨)
        deptNames, // axios: deptNames[]=A&deptNames[]=B 로 직렬화
        deptNamesCsv: deptNames.join(',') // 백엔드가 CSV만 받을 때 대비
      };

      const resp = await axios.get('/api/log/agent', {
        params: payload,
        headers: { Accept: 'application/json' }
      });

      if (isStale()) return; // 조건이 바뀐 뒤 도착한 응답은 버린다

      if (!Array.isArray(resp?.data.result)) {
        setDownloads([]);
        setDownloadsError('unexpected response format: expected array (downloads)');
        console.warn('expected array but got (downloads)', resp?.data.result);
        return;
      }

      // DB 예시: id, appName, version, company, userNm, ip, partname, created_at, updated_at
      const resolveDept = createDeptResolver();
      const normalized = resp.data.result.map((r) => {
        const created = r.created_at || r.createdAt || r.regDtt; // 날짜 필드 후보
        // 부서명 → 코드/부문 역매핑(표시용)
        const deptMatch = resolveDept(r.partname);

        return {
          // 화면 고정 컬럼
          div: 'desktop_get',
          regDtt: created,
          userNm: r.userNm || r.username || '',
          userId: r.keycode || '',
          sectorNm: deptMatch.sectorNm,
          deptNm: r.partname || deptMatch.deptName || '',
          _ts: toTs(created)
        };
      });

      // 서버에서 부서 필터가 이미 적용되므로 클라에선 연/월만 안전 체크.
      // 월 경계를 두 번만 계산하고 행별로는 숫자 비교만 한다.
      const monthStart = moment({ year: Number(year), month: Number(month) - 1, date: 1 }).startOf('day').valueOf();
      const monthEnd = moment(monthStart).add(1, 'month').valueOf();
      const filtered = normalized.filter((row) => Number.isFinite(row._ts) && row._ts >= monthStart && row._ts < monthEnd);
      setDownloads(filtered);
    } catch (err) {
      console.error(err);
      if (isStale()) return;
      setDownloadsError(err?.response?.data?.message || err.message || 'failed to fetch downloads');
      setDownloads([]);
    }
  };

  // ========================= watcher =========================
  useEffect(() => {
    if (!didInitRef.current) return;
    runFetches(sector, dept);
  }, [year, month, sector, dept]);

  // 두 소스를 각각 독립적으로 반영한다. 빠른 쪽(다운로드)이 먼저 화면에 그려지고
  // 느린 쪽(업로드)은 도착하는 대로 합쳐진다.
  const runFetches = (nextSector, nextDept) => {
    const reqId = ++reqIdRef.current;
    setPage(1);

    setDownloadsLoading(true);
    fetchDownloadsFromDb(nextSector, nextDept, reqId).finally(() => {
      if (reqId === reqIdRef.current) setDownloadsLoading(false);
    });

    setHistoryLoading(true);
    fetchHistory(nextSector, nextDept, reqId).finally(() => {
      if (reqId === reqIdRef.current) setHistoryLoading(false);
    });
  };

  // ========================= 메모 값 =========================
  const filteredDepts = useMemo(() => {
    if (sector === 'all') return [];
    return depts.filter((d) => d.sapGroupCode === sector);
  }, [depts, sector]);

  const sortByRegDttDesc = (a, b) => {
    const ta = a?._ts;
    const tb = b?._ts;
    if (Number.isFinite(ta) && Number.isFinite(tb)) return tb - ta;
    return 0;
  };

  const filteredByViewType = useMemo(() => {
    const uploads = Array.isArray(history) ? history : [];
    const downs = Array.isArray(downloads) ? downloads : [];
    if (viewType === 'upload') return uploads.slice().sort(sortByRegDttDesc);
    if (viewType === 'download') return downs.slice().sort(sortByRegDttDesc);
    return uploads.concat(downs).sort(sortByRegDttDesc);
  }, [history, downloads, viewType]);

  const totalRows = filteredByViewType.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  const pagedRows = useMemo(() => {
    if (filteredByViewType.length === 0) return [];
    const start = (page - 1) * pageSize;
    return filteredByViewType.slice(start, start + pageSize);
  }, [filteredByViewType, page, pageSize]);

  const uploadCount = Array.isArray(history) ? history.length : 0;
  const downloadCount = Array.isArray(downloads) ? downloads.length : 0;

  const showingFrom = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, totalRows);

  const colCount = fixedColumns.length;
  const xsPerCol = Math.max(1, Math.floor(12 / colCount));

  const sectorLabel = useMemo(() => {
    if (sector === 'all') return '전체';
    const found = sectors.find((s) => s.sapGroupCode === sector);
    return found ? found.sapGroupName : sector;
  }, [sector, sectors]);

  const deptLabel = useMemo(() => {
    if (dept === 'all') return '전체';
    const found = depts.find((d) => d.deptCd === dept);
    return found ? found.deptName : dept;
  }, [dept, depts]);

  // ========================= 핸들러 =========================
  const handlePageChange = (_e, value) => setPage(value);
  const handlePageSizeChange = (e) => {
    const next = Number(e.target.value);
    setPageSize(next);
    setPage(1);
  };
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const hardRefresh = () => runFetches(sector, dept);

  const handleExportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    // XLSX 생성은 동기 작업이라 그대로 실행하면 스피너가 그려지기 전에 UI가 멈춘다.
    // 한 프레임 양보해 스피너를 먼저 그린 뒤 시작한다.
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));

    try {
      buildAndSaveExcel();
    } finally {
      setExporting(false);
    }
  };

  const buildAndSaveExcel = () => {
    const rows = filteredByViewType;
    const headers = fixedColumns.map((c) => headerMap[c]);

    const summary1 = [`${year}년 ${String(month).padStart(2, '0')}월`];
    const summary2 = [`부문: ${sectorLabel}`, `부서:  ${deptLabel}`, `업로드 ${nf.format(uploadCount)} / 다운로드 ${nf.format(downloadCount)}`];

    const body = rows.map((r) =>
      fixedColumns.map((col) => {
        if (col === 'div') return mapDivToKor(r[col]);
        if (col === 'regDtt') {
          const m = moment(r[col]);
          return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : r[col] ?? '';
        }
        return r[col] ?? '';
      })
    );

    const aoa = [summary1, summary2, [], headers].concat(body);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // body가 수천 건일 때 Math.max(...spread)는 인자가 스택에 쌓여 터지므로 루프로 계산
    const maxLens = headers.map((h) => Math.max(String(h).length, 8));
    body.forEach((row) => {
      for (let i = 0; i < maxLens.length; i++) {
        const len = String(row[i] ?? '').length;
        if (len > maxLens[i]) maxLens[i] = len;
      }
    });
    ws['!cols'] = maxLens.map((maxLen) => ({ wch: Math.min(Math.max(maxLen + 2, 10), 60) }));

    XLSX.utils.book_append_sheet(wb, ws, 'AgentLog');

    const ts = moment().format('YYYYMMDD_HHmmss');
    const fname = `AgentLog_${year}-${String(month).padStart(2, '0')}_${sectorLabel}_${deptLabel}_${ts}.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  // ========================= 렌더 =========================
  return (
    <Stack spacing={2} sx={{ maxWidth: 1200, position: 'relative' }}>
      {downloadsLoading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.65)',
            zIndex: 1300,
            backdropFilter: 'blur(1px)'
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* 필터 영역 */}
      <CmmContainer sx={{ width: '100%', display: 'block' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* 연도 */}
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="year-label">연도</InputLabel>
            <Select labelId="year-label" name="year" value={year || ''} label="연도" onChange={(e) => setYear(String(e.target.value))}>
              {years.map((y) => (
                <MenuItem key={y} value={String(y)}>
                  {y}년
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 월 */}
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="month-label">월</InputLabel>
            <Select labelId="month-label" name="month" value={Number(month) || ''} label="월" onChange={(e) => setMonth(Number(e.target.value))}>
              {months.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}월
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 부문 */}
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel id="sector-label">부문</InputLabel>
            <Select
              labelId="sector-label"
              name="sector"
              value={sector}
              label="부문"
              onChange={(e) => {
                setSector(String(e.target.value));
                setDept('all'); // 부문 변경 시 부서 초기화
              }}
            >
              <MenuItem value="all">전체</MenuItem>
              {sectors.map((s) => (
                <MenuItem key={s.sapGroupCode} value={s.sapGroupCode}>
                  {s.sapGroupName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 부서 */}
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="dept-label">부서</InputLabel>
            <Select labelId="dept-label" name="dept" value={dept} label="부서" onChange={(e) => setDept(String(e.target.value))} disabled={sector === 'all'}>
              <MenuItem value="all">전체</MenuItem>
              {filteredDepts.map((d) => (
                <MenuItem key={d.deptCd} value={d.deptCd}>
                  {d.deptName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 구분 */}
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel id="viewtype-label">구분</InputLabel>
            <Select
              labelId="viewtype-label"
              name="viewtype"
              value={viewType}
              label="구분"
              onChange={(e) => {
                const val = e.target.value;
                setViewType(val);
                setPage(1);
              }}
            >
              <MenuItem value="all">전체</MenuItem>
              <MenuItem value="upload">업로드</MenuItem>
              <MenuItem value="download">다운로드</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </CmmContainer>

      {/* 타이틀 */}
      <CmmContainer sx={{ width: '100%', display: 'block' }}>
        <Typography component="h1" variant="h3" sx={{ textAlign: 'center' }}>
          Agent Log
        </Typography>
      </CmmContainer>

      {/* 본문 */}
      <Box sx={{ flexGrow: 1 }}>
        <Stack>
          <Grid item xs={12}>
            <CmmContainer>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CmmSubTitle title={`업로드 ${nf.format(uploadCount)} / 다운로드 ${nf.format(downloadCount)}`} />
                  {historyLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        업로드 이력 불러오는 중…
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" onClick={hardRefresh}>
                    새로고침
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                    onClick={handleExportExcel}
                    disabled={historyLoading || downloadsLoading || exporting}
                  >
                    {exporting ? '생성 중…' : '엑셀 다운로드'}
                  </Button>
                </Box>
              </Box>

              <Box sx={{ position: 'relative', minHeight: 200 }} id="history-table-top">
                <Box sx={{ p: 2 }}>
                  {(historyError || downloadsError) && (
                    <Typography color="error">
                      {historyError ? `업로드: ${historyError}` : ''}
                      {historyError && downloadsError ? ' / ' : ''}
                      {downloadsError ? `다운로드: ${downloadsError}` : ''}
                    </Typography>
                  )}

                  {!historyError && !downloadsError && filteredByViewType.length === 0 && <Typography>데이터 없음</Typography>}

                  {filteredByViewType.length > 0 && (
                    <Paper variant="outlined" sx={{ width: '100%', overflow: 'auto' }}>
                      {/* Header */}
                      <Grid container sx={{ bgcolor: 'grey.100', p: 1 }}>
                        {fixedColumns.map((col) => (
                          <Grid item key={col} xs={xsPerCol} sx={{ pr: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {headerMap[col]}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>

                      <Divider />

                      {/* Rows */}
                      {pagedRows.map((row, rowIndex) => {
                        const globalIndex = (page - 1) * pageSize + rowIndex;
                        const key = `${row.regDtt || ''}__${row.userId || ''}__${row.div || ''}__${globalIndex}`;
                        return (
                          <Grid container key={key} sx={{ p: 1, '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                            {fixedColumns.map((col) => (
                              <Grid item key={col} xs={xsPerCol} sx={{ pr: 1 }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: 13 }}>
                                  {renderCellValue(row[col], col)}
                                </Typography>
                              </Grid>
                            ))}
                          </Grid>
                        );
                      })}
                    </Paper>
                  )}

                  {/* paging controls */}
                  {filteredByViewType.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 2,
                        gap: 2,
                        flexWrap: 'wrap'
                      }}
                    >
                      <Box>
                        <Typography variant="body2">{`${showingFrom} - ${showingTo} of ${totalRows}`}</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 96 }}>
                          <InputLabel id="pagesize-label">페이지</InputLabel>
                          <Select labelId="pagesize-label" value={pageSize} label="페이지" onChange={handlePageSizeChange}>
                            {pageSizeOptions.map((s) => (
                              <MenuItem key={s} value={s}>
                                {s} / page
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" showFirstButton showLastButton />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </CmmContainer>
          </Grid>
        </Stack>
      </Box>
    </Stack>
  );
}
